import React from 'react';

import { EBadgeColors } from '@domain/enums/components/EBadge';
import { EAdSenseProvider } from '@domain/enums/common/EAdSense';
import { ITableRowProps } from '@domain/interfaces/integrations/marketing/facebook/ITable';
import { IParams } from '@domain/interfaces/IParams';

import { useConfig } from '@helpers/hooks/useConfig';
import { useFacebookAdAccounts } from '@helpers/hooks/common/integrations/marketing/facebook/useFacebookAdAccounts';
import { useToast } from '@helpers/hooks/useToast';
import { useSynchronization } from '@helpers/hooks/common/useSynchronization';
import { useParams } from 'react-router-dom';

import Badge from '@components/common/core/DataDisplay/Badge';

import * as S from './styles';

const Row: React.FC<ITableRowProps> = ({ adSenseAccount }) => {
  const { analytics } = useConfig();

  const { storeAliasId } = useParams<IParams>();

  const { updateAdSenseAccount } = useFacebookAdAccounts();

  const { toast } = useToast();
  const {
    currentSynchronizations,
    synchronizationsQueue,
    finishedSynchronizations,
    failedSynchronizations,
    handleFailedSynchronizations,
    handleFinishedSynchronizations,
  } = useSynchronization();

  const currentSynchronizationsArray = currentSynchronizations
    ? [...currentSynchronizations.synchronizations]
    : [];
  const mergedSynchronizations = [...synchronizationsQueue, ...currentSynchronizationsArray];

  const [isUpdatingAdAccount, setIsUpdatingAdAccount] = React.useState<boolean>(false);
  const [isSyncing, setIsSyncing] = React.useState<boolean>(false);

  const switchRef = React.useRef<HTMLInputElement>(null);

  const onSwitchChange = React.useCallback(
    async event => {
      try {
        if (adSenseAccount.is_blocked_by_downgrade) {
          toast.error('Não é possível realizar a ação.');

          if (switchRef.current) {
            switchRef.current.checked = !switchRef.current.checked;
          }

          return;
        }

        if (!event.target.checked) {
          setIsUpdatingAdAccount(true);
        }

        const data = {
          ...adSenseAccount,
          is_active: event.target.checked,
        };

        await updateAdSenseAccount(adSenseAccount.alias_id, data);

        setIsUpdatingAdAccount(false);

        if (switchRef.current) {
          if (switchRef.current.checked) {
            analytics?.track(
              'Onboarding Ads Account Activated',
              {
                provider: EAdSenseProvider.FACEBOOK,
              },
              { context: { groupId: storeAliasId } },
            );
          }
        }
      } catch (error: any) {
        if (switchRef.current) {
          switchRef.current.checked = !switchRef.current.checked;
        }

        setIsUpdatingAdAccount(false);
        toast.error(error?.response?.message?.data);
      }
    },
    [adSenseAccount, toast, updateAdSenseAccount, analytics, storeAliasId],
  );

  const parseIsOperationalName = React.useCallback(() => {
    if (adSenseAccount.is_blocked_by_downgrade) return 'Inativo';

    if (adSenseAccount.is_operational) return 'Operacional';

    return 'Não operacional';
  }, [adSenseAccount]);

  const getBadgeColor = React.useCallback(() => {
    if (adSenseAccount.is_blocked_by_downgrade) return EBadgeColors.YELLOW;

    if (adSenseAccount.is_operational) return EBadgeColors.PRIMARY;

    return EBadgeColors.RED;
  }, [adSenseAccount]);

  React.useEffect(() => {
    if (mergedSynchronizations.length) {
      const foundSynchronization = mergedSynchronizations.find(
        synchronization => synchronization.externalId === adSenseAccount.alias_id,
      );

      setIsSyncing(Boolean(foundSynchronization));
    } else {
      setIsSyncing(false);
    }
  }, [adSenseAccount, mergedSynchronizations]);

  React.useEffect(() => {
    if (finishedSynchronizations.length) {
      const filteredFinishedSynchronizations = finishedSynchronizations.filter(
        synchronization => synchronization.externalId === adSenseAccount.alias_id,
      );

      if (filteredFinishedSynchronizations.length) {
        const newFinishedSynchronzation = finishedSynchronizations.filter(
          synchronization => synchronization.externalId !== adSenseAccount.alias_id,
        );

        handleFinishedSynchronizations(newFinishedSynchronzation);
      }
    }
  }, [finishedSynchronizations, handleFinishedSynchronizations, adSenseAccount]);

  React.useEffect(() => {
    if (failedSynchronizations.length) {
      const filteredFailedSynchronizations = failedSynchronizations.filter(
        synchronization => synchronization.externalId === adSenseAccount.alias_id,
      );

      if (filteredFailedSynchronizations.length) {
        if (switchRef?.current) {
          switchRef.current.checked = false;
        }

        const newFailedSynchronization = failedSynchronizations.filter(
          synchronization => synchronization.externalId !== adSenseAccount.alias_id,
        );

        handleFailedSynchronizations(newFailedSynchronization);
      }
    }
  }, [toast, switchRef, failedSynchronizations, adSenseAccount, handleFailedSynchronizations]);

  const isAdAccountBlockedByDowngrade = adSenseAccount.is_blocked_by_downgrade;

  return (
    <S.Table.Row borderBottom>
      <S.Table.Data>
        <S.Switch
          name={`switch-${adSenseAccount.id}`}
          id={`switch-${adSenseAccount.id}`}
          onChange={!isSyncing ? onSwitchChange : () => null}
          isLoading={isUpdatingAdAccount}
          defaultChecked={adSenseAccount.is_active}
          disabled={isSyncing || isUpdatingAdAccount}
          innerRef={switchRef}
        />
      </S.Table.Data>

      <S.Table.Data>
        <S.InfosWrapper>
          <S.AccountInfo>{adSenseAccount.provider_id}</S.AccountInfo>
          <S.Description>ID da conta</S.Description>
        </S.InfosWrapper>
      </S.Table.Data>

      <S.Table.Data>
        <S.InfosWrapper>
          <S.AccountInfo>{adSenseAccount.name}</S.AccountInfo>
          <S.Description>Nome</S.Description>
        </S.InfosWrapper>
      </S.Table.Data>

      {/* <S.Table.Data align={EHeadAlign.RIGHT}>
        <S.InfosWrapper>
          <S.AccountInfo>{`R$ ${numberFormatter(
            adSenseAccount.ad_sense_account_insight.spend_amount,
            0,
          )}`}</S.AccountInfo>
          <S.Description>Gasto dos últimos 7 dias</S.Description>
        </S.InfosWrapper>
      </S.Table.Data> */}

      <S.Table.Data>
        <S.Tooltip>
          <S.Tooltip.Trigger>
            <Badge color={getBadgeColor()}>{parseIsOperationalName()}</Badge>
          </S.Tooltip.Trigger>

          {isAdAccountBlockedByDowngrade && (
            <S.Tooltip.Content side="top" sideOffset={5}>
              <S.TooltipContent>
                <S.TooltipText>
                  Essa conta de anúncio foi inativada{' '}
                  <strong>após a realização do Downgrade.</strong>
                  <br />
                  Os dados retroativos dessa conta <strong>não foram excluídos.</strong>
                </S.TooltipText>
              </S.TooltipContent>

              <S.Tooltip.Arrow />
            </S.Tooltip.Content>
          )}
        </S.Tooltip>
      </S.Table.Data>
    </S.Table.Row>
  );
};

export default Row;
